import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Rating,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import ShareIcon from '@mui/icons-material/Share';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { buildImageUrl } from '../config/apiConfig';

const FALLBACK_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300"%3E%3Crect fill="%23f0f0f0" width="400" height="300"/%3E%3Ctext x="50%25" y="50%25" font-size="16" fill="%23999" text-anchor="middle" dominant-baseline="middle"%3EProperty Image%3C/text%3E%3C/svg%3E';

const PropertyDetails = ({ open, property, onClose }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [shareAnchorEl, setShareAnchorEl] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  if (!property) {
    return (
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>No Property Data</DialogTitle>
        <DialogContent>
          <Typography>Unable to load property details.</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  const {
    id,
    title = 'Untitled Property',
    description = 'No description',
    location = 'Location not specified',
    price = 'N/A',
    rating = 4.5,
    beds = 0,
    baths = 0,
    area = 'N/A',
    category = 'other',
    status = 'active',
    photo = null,
    images = [],
    ownerName = 'Not Available',
    ownerPhone = 'Not Available',
    ownerEmail = 'Not Available',
  } = property;

  // Get all available images and build proper URLs
  const processedImages = React.useMemo(() => {
    const processed = [];

    // Add images from array
    if (Array.isArray(images) && images.length > 0) {
      images.forEach((img) => {
        if (img?.image_path) {
          const url = buildImageUrl(img.image_path, 'properties');
          console.log('📸 Processed image:', url);
          processed.push({ 
            image_path: url, 
            is_primary: img.is_primary,
            original: img 
          });
        }
      });
    }

    // Add photo field if no images
    if (processed.length === 0 && photo) {
      const url = buildImageUrl(photo, 'properties');
      console.log('📸 Using photo field:', url);
      processed.push({ 
        image_path: url, 
        is_primary: 1,
        original: { photo } 
      });
    }

    // Return processed images or fallback
    return processed.length > 0 ? processed : [{ image_path: FALLBACK_IMAGE, is_primary: 1 }];
  }, [images, photo]);

  const mainImage = processedImages[selectedImageIndex];

  const handleShareClick = (event) => {
    setShareAnchorEl(event.currentTarget);
  };

  const handleShareClose = () => {
    setShareAnchorEl(null);
  };

  const generateShareText = () => {
    return `Check out this property: ${title}\n📍 ${location}\n💰 ₹${parseFloat(price).toLocaleString('en-IN')}\n🛏️ ${beds} Beds | 🚿 ${baths} Baths | 📐 ${area}\n\n${description}\n\nOwner: ${ownerName}\n📱 ${ownerPhone}`;
  };

  const handleShareWhatsApp = () => {
    const text = encodeURIComponent(generateShareText());
    window.open(`https://wa.me/?text=${text}`, '_blank');
    setSnackbarMessage('✅ Shared on WhatsApp!');
    setSnackbarOpen(true);
    handleShareClose();
  };

  const handleShareEmail = () => {
    const subject = encodeURIComponent(`Check out: ${title}`);
    const body = encodeURIComponent(generateShareText());
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    setSnackbarMessage('📧 Opening email client...');
    setSnackbarOpen(true);
    handleShareClose();
  };

  const handleShareCopy = () => {
    navigator.clipboard.writeText(generateShareText());
    setSnackbarMessage('✅ Property details copied to clipboard!');
    setSnackbarOpen(true);
    handleShareClose();
  };

  const handleShareNative = () => {
    if (navigator.share) {
      navigator.share({
        title: title,
        text: generateShareText(),
        url: window.location.href
      }).catch(err => console.log('Error sharing:', err));
    }
    handleShareClose();
  };

  const handleImageError = (e) => {
    console.error('❌ Image failed to load:', e.target.src);
    e.target.src = FALLBACK_IMAGE;
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
          Property Details
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={handleShareClick}
            sx={{ color: '#667eea', '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.1)' } }}
          >
            <ShareIcon />
          </IconButton>
          <IconButton 
            aria-label="close" 
            onClick={onClose}
            sx={{ color: 'text.secondary', '&:hover': { bgcolor: '#f5f5f5' } }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Share Menu */}
      <Menu
        anchorEl={shareAnchorEl}
        open={Boolean(shareAnchorEl)}
        onClose={handleShareClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleShareWhatsApp}>
          <WhatsAppIcon sx={{ mr: 1, color: '#25D366' }} fontSize="small" />
          Share on WhatsApp
        </MenuItem>
        <MenuItem onClick={handleShareEmail}>
          <EmailIcon sx={{ mr: 1, color: '#667eea' }} fontSize="small" />
          Share via Email
        </MenuItem>
        <MenuItem onClick={handleShareCopy}>
          Share Property Details
        </MenuItem>
        {navigator.share && (
          <MenuItem onClick={handleShareNative}>
            More Options
          </MenuItem>
        )}
      </Menu>

      <Divider />

      <DialogContent sx={{ py: 3 }}>
        {/* Property Images - Large View with Thumbnails */}
        {processedImages && processedImages.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              📸 Images
            </Typography>
            
            {/* Main Large Image */}
            <Box 
              sx={{ 
                width: '100%', 
                height: 350, 
                borderRadius: 2, 
                overflow: 'hidden', 
                mb: 1.5,
                bgcolor: '#f0f0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid #e0e0e0'
              }}
            >
              <img
                src={mainImage?.image_path || FALLBACK_IMAGE}
                alt={`Property ${selectedImageIndex + 1}`}
                onError={handleImageError}
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover',
                  cursor: 'pointer',
                  transition: 'transform 0.3s'
                }}
              />
            </Box>

            {/* Thumbnail Images */}
            <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
              {processedImages.map((image, index) => (
                <Box
                  key={index}
                  onClick={() => setSelectedImageIndex(index)}
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    border: selectedImageIndex === index ? '3px solid #667eea' : '2px solid #e0e0e0',
                    transition: 'all 0.3s',
                    flexShrink: 0,
                    '&:hover': {
                      borderColor: '#667eea',
                      transform: 'scale(1.05)',
                      boxShadow: 2
                    }
                  }}
                >
                  <img
                    src={image.image_path}
                    alt={`Thumbnail ${index + 1}`}
                    onError={handleImageError}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </Box>
              ))}
            </Box>

            {/* Image Counter */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {selectedImageIndex + 1} of {processedImages.length}
            </Typography>
          </Box>
        )}

        {/* Title and Status */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, transition: 'color 0.3s', '&:hover': { color: '#667eea' } }}>
              {title}
            </Typography>
            <Chip 
              label={status.toUpperCase()} 
              color={status === 'active' ? 'success' : 'default'}
              variant="outlined"
              sx={{ transition: 'all 0.3s', '&:hover': { transform: 'scale(1.05)' } }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            📍 {location}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Price and Rating */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, p: 1.5, borderRadius: 1, transition: 'all 0.3s', '&:hover': { bgcolor: '#f9f9f9' } }}>
          <Typography variant="h6" sx={{ color: '#667eea', fontWeight: 700, transition: 'color 0.3s', '&:hover': { color: '#5568d3' } }}>
            ₹{parseFloat(price).toLocaleString('en-IN')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, transition: 'all 0.3s', '&:hover': { transform: 'scale(1.05)' } }}>
            <Rating value={parseFloat(rating) || 4.5} readOnly />
            <Typography variant="body2" color="text.secondary">
              ({parseFloat(rating) || 4.5} / 5)
            </Typography>
          </Box>
        </Box>

        {/* Description */}
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, transition: 'all 0.3s', '&:hover': { bgcolor: '#f5f5f5' } }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Description
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {description}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Property Features */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            Property Features
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {beds > 0 && (
              <Chip 
                label={`🛏️ ${beds} Bedrooms`} 
                sx={{ transition: 'all 0.3s', '&:hover': { transform: 'scale(1.1)', boxShadow: 2 } }}
              />
            )}
            {baths > 0 && (
              <Chip 
                label={`🚿 ${baths} Bathrooms`} 
                sx={{ transition: 'all 0.3s', '&:hover': { transform: 'scale(1.1)', boxShadow: 2 } }}
              />
            )}
            {area && area !== 'N/A' && (
              <Chip 
                label={`📐 ${area}`} 
                sx={{ transition: 'all 0.3s', '&:hover': { transform: 'scale(1.1)', boxShadow: 2 } }}
              />
            )}
            {category && (
              <Chip 
                label={`📁 ${category}`} 
                variant="outlined" 
                sx={{ transition: 'all 0.3s', '&:hover': { transform: 'scale(1.1)', boxShadow: 2 } }}
              />
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Owner Contact Information */}
        <Box sx={{ mb: 2, bgcolor: '#f0f7ff', p: 2.5, borderRadius: 2, border: '2px solid #667eea', transition: 'all 0.3s', '&:hover': { boxShadow: 3, transform: 'translateY(-2px)', borderColor: '#5568d3', bgcolor: '#f5fbff' } }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#333', fontSize: '1.05rem' }}>
            👤 Owner Information
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Owner Name */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1, transition: 'all 0.3s', '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.1)' } }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, minWidth: '100px' }}>
                Name:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#333' }}>
                {ownerName}
              </Typography>
            </Box>

            {/* Owner Phone */}
            {ownerPhone && ownerPhone !== 'Not Available' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1, transition: 'all 0.3s', '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.1)', transform: 'translateX(4px)' } }}>
                <PhoneIcon sx={{ color: '#667eea', fontSize: '1.3rem' }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, minWidth: '100px' }}>
                  Phone:
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ fontWeight: 700, color: '#667eea', cursor: 'pointer', textDecoration: 'none', transition: 'all 0.3s', '&:hover': { textDecoration: 'underline', color: '#5568d3' } }}
                  component="a"
                  href={`tel:${ownerPhone}`}
                >
                  {ownerPhone}
                </Typography>
              </Box>
            )}

            {/* Owner Email */}
            {ownerEmail && ownerEmail !== 'Not Available' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1, transition: 'all 0.3s', '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.1)', transform: 'translateX(4px)' } }}>
                <EmailIcon sx={{ color: '#667eea', fontSize: '1.3rem' }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, minWidth: '100px' }}>
                  Email:
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ fontWeight: 600, color: '#667eea', cursor: 'pointer', textDecoration: 'none', transition: 'all 0.3s', '&:hover': { textDecoration: 'underline', color: '#5568d3' } }}
                  component="a"
                  href={`mailto:${ownerEmail}`}
                >
                  {ownerEmail}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Property ID */}
        <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1, transition: 'all 0.3s', '&:hover': { bgcolor: '#efefef', boxShadow: 1 } }}>
          <Typography variant="caption" color="text.secondary">
            Property ID: <strong>{id}</strong>
          </Typography>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button 
          onClick={onClose} 
          color="primary"
          sx={{ transition: 'all 0.3s', '&:hover': { transform: 'translateY(-2px)' } }}
        >
          Close
        </Button>
        {ownerPhone && ownerPhone !== 'Not Available' && (
          <Button 
            variant="contained" 
            color="primary"
            startIcon={<PhoneIcon />}
            href={`tel:${ownerPhone}`}
            sx={{ transition: 'all 0.3s', '&:hover': { transform: 'translateY(-3px)', boxShadow: 4 } }}
          >
            Call Owner
          </Button>
        )}
      </DialogActions>

      {/* Snackbar for Share Feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default PropertyDetails;